import { Card } from '@/components/ui-kit/card';
import { AiModelsList } from './aimodels-list';
import { useAIModelsQueryParams, useSeedProviders } from '@/modules/gpt-chats/hooks/use-aimodel';
import { AIModelsFilterToolbar } from '@/modules/gpt-chats/components/aimodels/aimodel-filter-toolbar/aimodel-filter-toolbar';
import { IProvider } from '@/modules/gpt-chats/types/aimodel.service.type';

import {
  createCustomProvider,
  ProviderToPlatformMap,
  ServicePlatform,
} from '@/modules/gpt-chats/utils/aimodel-provider.utils';
// import { normalizeProviderList } from "@/modules/gpt-chats/utils/aimodel-normalizer.utils";
import { Skeleton } from '@/components/ui-kit/skeleton';

// Skeleton grid that mimics ProviderCard layout
const ProviderGridSkeleton = () => {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="w-75 flex flex-col gap-4 rounded-md border px-4 py-5">
          <div className="mb-0 flex w-full flex-row justify-between">
            <div className="flex flex-row gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-sm border p-2">
                <Skeleton className="h-8 w-8" />
              </div>
              <div className="flex flex-col gap-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
            <Skeleton className="h-5 w-5" />
          </div>
          <div>
            <Skeleton className="mb-1 h-4 w-full" />
            <Skeleton className="mb-1 h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
};

import { useTranslation } from 'react-i18next';

export const AIModels = () => {
  const { t } = useTranslation();
  const { data: providers = [], isLoading } = useSeedProviders();

  const allProviders = [...providers, createCustomProvider()];

  const officialApiModels = allProviders.filter((p) => {
    if (!p || !p.Provider) return false;
    return ProviderToPlatformMap[p.Provider.toLowerCase()] === ServicePlatform.OFFICIAL_API;
  });

  const openDeploymentModels = allProviders.filter((p) => {
    if (!p || !p.Provider) return false;
    return ProviderToPlatformMap[p.Provider.toLowerCase()] != ServicePlatform.OFFICIAL_API;
  });

  const { queryParams } = useAIModelsQueryParams();
  const search = queryParams.search.toLowerCase();
  const selectedTypes = queryParams.types;

  const matchSearch = (p: IProvider) => p.Provider.toLowerCase().includes(search);

  const shouldShowOfficial = selectedTypes.length === 0 || selectedTypes.includes('official');
  const shouldShowOpen = selectedTypes.length === 0 || selectedTypes.includes('open');

  const filteredOfficial = officialApiModels.filter(matchSearch);
  const filteredOpen = openDeploymentModels.filter(matchSearch);

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-2xl font-semibold text-high-emphasis">{t('MODELS')}</h3>

      <Card className="flex flex-col gap-5 p-5">
        <AIModelsFilterToolbar />

        {isLoading ? (
          <div className="flex flex-col gap-5">
            {shouldShowOfficial && (
              <div className="flex flex-col gap-5">
                <p className="border-b-1 border-gray-150 flex w-full border-b pb-2 text-base font-normal text-low-emphasis">
                  {t('OFFICIAL_API')}
                </p>
                <ProviderGridSkeleton />
              </div>
            )}

            {shouldShowOpen && (
              <div className="flex flex-col gap-5">
                <p className="border-b-1 border-gray-150 flex w-full border-b pb-2 text-base font-normal text-low-emphasis">
                  {t('OPEN_DEPLOYMENT')}
                </p>
                <ProviderGridSkeleton />
              </div>
            )}
          </div>
        ) : (
          <>
            {shouldShowOfficial && (
              <AiModelsList servicePlatform={t('OFFICIAL_API')} providerList={filteredOfficial} />
            )}

            {shouldShowOpen && (
              <AiModelsList servicePlatform={t('OPEN_DEPLOYMENT')} providerList={filteredOpen} />
            )}
          </>
        )}
      </Card>
    </div>
  );
};
